/**
 * Author-only file dropper for journey map `file` items.
 *
 * Files live in S3 and are tracked as `JourneyMapFile` rows scoped to the journey map. The
 * step stores the row id in `content_ref_id`, so a replace (which keeps the row id and bumps
 * its version) never breaks the step's reference — learners always resolve the current bytes.
 *
 * Two states are not failures and are handled explicitly rather than as errors:
 *  - an unsaved draft has no journey map id, so there is nothing to scope an upload to;
 *  - storage may be unconfigured entirely (the API answers 503), in which case the author
 *    falls back to typing a reference by hand, which is what this field was before.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import {
  JourneyMapFileRecord,
  JourneyMapFileStorageUnavailableError,
  deleteJourneyMapFile,
  formatFileSize,
  listJourneyMapFiles,
  openJourneyMapFile,
  replaceJourneyMapFile,
  uploadJourneyMapFile,
} from '../../../../support/journeyMap/files';
import { detectMp4FastStart } from '../../../../support/videoFastStart';

type Props = {
  /** Null while the journey map is an unsaved draft. */
  journeyMapId: string | null;
  /** Null for an item that has not been persisted yet; the file still attaches to the map. */
  journeyMapItemId?: number | string | null;
  /** `content_ref_id` of the item: the id of the `JourneyMapFile` row in use. */
  value?: string | number | null;
  onChange: (record: JourneyMapFileRecord | null) => void;
  /** Free-text fallback used when storage is not configured. */
  onManualReferenceChange?: (value: string) => void;
};

const JourneyMapFileDropper: React.FC<Props> = ({
  journeyMapId,
  journeyMapItemId = null,
  value = null,
  onChange,
  onManualReferenceChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  const [record, setRecord] = useState<JourneyMapFileRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'upload' | 'replace' | 'delete' | 'download' | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Non-blocking advisory (e.g. a video that isn't faststart-optimized). The upload still
  // succeeded; this is guidance, not a failure.
  const [warning, setWarning] = useState<string | null>(null);
  const [storageUnavailable, setStorageUnavailable] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fileId = value == null || value === '' ? null : String(value);

  // The row is looked up rather than trusted from provider_config so the panel shows the
  // current version and file name even when another author replaced the file elsewhere.
  useEffect(() => {
    let active = true;

    const run = async () => {
      if (!journeyMapId || !fileId) {
        if (active) setRecord(null);
        return;
      }

      setLoading(true);
      try {
        const rows = await listJourneyMapFiles(journeyMapId, { includeInactive: true });
        if (!active) return;

        setRecord(rows.find((row) => String(row.id) === fileId) || null);
        setStorageUnavailable(false);
      } catch (err: any) {
        if (!active) return;

        if (err instanceof JourneyMapFileStorageUnavailableError) {
          setStorageUnavailable(true);
        }

        setRecord(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [journeyMapId, fileId]);

  const handleFailure = useCallback((err: any, fallback: string) => {
    if (err instanceof JourneyMapFileStorageUnavailableError) {
      setStorageUnavailable(true);
      setError(null);
      return;
    }

    setError(String(err?.message || fallback));
  }, []);

  /**
   * Advise (never block) when a just-uploaded video is not faststart-optimized — the usual cause
   * of a video that will not start playing until it has fully downloaded. Local, best-effort;
   * anything inconclusive stays silent.
   */
  const checkFastStart = useCallback(async (file: File) => {
    if (!file.type.startsWith('video/')) return;

    const result = await detectMp4FastStart(file);
    if (result === 'not-faststart') {
      setWarning(
        'This video isn’t web-optimized, so it may be slow to start playing for learners. ' +
          'Re-encode it with “faststart” (e.g. ffmpeg -movflags +faststart) and re-upload for ' +
          'instant playback.',
      );
    }
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!journeyMapId || !file) return;

      setError(null);
      setWarning(null);
      setProgress(0);
      setBusy('upload');
      try {
        const created = await uploadJourneyMapFile(journeyMapId, file, {
          journeyMapItemId: journeyMapItemId ?? undefined,
          onProgress: (p) => setProgress(p.percent),
        });

        setRecord(created);
        onChange(created);
        void checkFastStart(file);
      } catch (err: any) {
        handleFailure(err, `Unable to upload ${file.name}.`);
      } finally {
        setBusy(null);
      }
    },
    [journeyMapId, journeyMapItemId, onChange, handleFailure, checkFastStart],
  );

  const handleReplace = useCallback(
    async (file: File) => {
      if (!journeyMapId || !record?.id || !file) return;

      setError(null);
      setWarning(null);
      setProgress(0);
      setBusy('replace');
      try {
        const updated = await replaceJourneyMapFile(journeyMapId, record.id, file, (p) =>
          setProgress(p.percent),
        );
        setRecord(updated);
        onChange(updated);
        void checkFastStart(file);
      } catch (err: any) {
        handleFailure(err, `Unable to replace ${record.file_name}.`);
      } finally {
        setBusy(null);
      }
    },
    [journeyMapId, record, onChange, handleFailure, checkFastStart],
  );

  const handleDelete = useCallback(async () => {
    if (!record?.id) return;

    setError(null);
    setWarning(null);
    setBusy('delete');
    try {
      await deleteJourneyMapFile(record.id);
      setRecord(null);
      onChange(null);
    } catch (err: any) {
      handleFailure(err, 'Unable to delete the file.');
    } finally {
      setBusy(null);
    }
  }, [record, onChange, handleFailure]);

  const handleDownload = useCallback(async () => {
    if (!record?.id) return;

    setError(null);
    setBusy('download');
    try {
      await openJourneyMapFile(record);
    } catch (err: any) {
      handleFailure(err, 'Unable to open the file.');
    } finally {
      setBusy(null);
    }
  }, [record, handleFailure]);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);

      const dropped = event.dataTransfer?.files?.[0];
      if (!dropped) return;

      if (record?.id) {
        void handleReplace(dropped);
        return;
      }

      void handleUpload(dropped);
    },
    [record, handleReplace, handleUpload],
  );

  const detailLine = useMemo(() => {
    if (!record) return '';

    const parts = [formatFileSize(record.size_bytes), record.content_type].filter(Boolean);

    if (Number(record.version || 1) > 1) parts.push(`v${record.version}`);
    if (record.uploaded_by_email) parts.push(String(record.uploaded_by_email));

    return parts.join(' · ');
  }, [record]);

  if (!journeyMapId) {
    return (
      <Alert severity="info">
        Save the journey map before uploading files. Files are stored against the saved map, so
        there is nowhere to put them until it exists.
      </Alert>
    );
  }

  if (storageUnavailable) {
    return (
      <Stack spacing={1}>
        <Alert severity="warning">
          File storage is not configured for this environment, so uploads are unavailable. You can
          still record a reference by hand.
        </Alert>
        <TextField
          label="File Reference"
          size="small"
          value={fileId || ''}
          onChange={(event) => onManualReferenceChange?.(event.target.value)}
          fullWidth
          helperText="Store an S3 key, file id, or other backend file reference."
        />
      </Stack>
    );
  }

  const isBusy = busy !== null;

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Attached File
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading file…
          </Typography>
        </Stack>
      ) : record ? (
        <Box
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          sx={{
            p: 1.25,
            border: '1px solid',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            backgroundColor: dragActive ? 'action.hover' : 'background.default',
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {record.file_name}
                </Typography>
                {detailLine ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {detailLine}
                  </Typography>
                ) : null}
                {!record.is_active ? (
                  <Typography variant="caption" color="error" display="block">
                    This file has been deleted from storage.
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleDownload}
                disabled={isBusy || !record.is_active}
              >
                {busy === 'download' ? 'Opening…' : 'Download'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHorizOutlinedIcon />}
                onClick={() => replaceInputRef.current?.click()}
                disabled={isBusy}
              >
                {busy === 'replace' ? `Replacing… ${progress}%` : 'Replace'}
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDelete}
                disabled={isBusy}
              >
                {busy === 'delete' ? 'Deleting…' : 'Delete'}
              </Button>
            </Stack>

            {busy === 'replace' ? (
              <LinearProgress variant="determinate" value={progress} />
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Replacing keeps this step pointing at the same file, so learners immediately get the
              new version.
            </Typography>
          </Stack>

          <input
            ref={replaceInputRef}
            type="file"
            hidden
            onChange={(event) => {
              const selected = event.target.files?.[0];
              event.target.value = '';
              if (selected) void handleReplace(selected);
            }}
          />
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            textAlign: 'center',
            cursor: isBusy ? 'default' : 'pointer',
            border: '1px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            backgroundColor: dragActive ? 'action.hover' : 'background.default',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          {busy === 'upload' ? (
            <Box sx={{ width: '100%', px: 1 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75 }}
              >
                Uploading… {progress}%
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUploadOutlinedIcon color="action" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Drop a file here
              </Typography>
              <Typography variant="caption" color="text.secondary">
                or <Link component="span">browse</Link> to choose one
              </Typography>
            </>
          )}

          <input
            ref={inputRef}
            type="file"
            hidden
            onChange={(event) => {
              const selected = event.target.files?.[0];
              event.target.value = '';
              if (selected) void handleUpload(selected);
            }}
          />
        </Box>
      )}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!error && warning ? (
        <Alert severity="warning" onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      ) : null}
    </Stack>
  );
};

export default JourneyMapFileDropper;
